import { Tooltip } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useConfigurableContext } from './ConfigurableContext/ConfigurableContext'

interface ProductConfigurationGridProps {
  sku: string
}

export function ProductConfigurationGrid({ sku }: ProductConfigurationGridProps) {
  const { options, getVariants, selection, select } = useConfigurableContext(sku)

  const allVariants = useMemo(() => getVariants({}), [getVariants])

  const sortedOptions = useMemo(
    () => [...(options ?? [])].sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0)),
    [options],
  )

  const availableUids = useMemo(() => {
    if (!allVariants.length) return null
    const available = new Set<string>()
    for (const option of sortedOptions) {
      if (!option?.attribute_code || !option.values) continue
      for (const value of option.values) {
        if (!value?.uid) continue
        const testSel = { ...selection, [option.attribute_code]: value.uid }
        const hasVariant = allVariants.some((v) =>
          Object.entries(testSel).every(([code, uid]) =>
            v?.attributes?.some((a) => a?.code === code && a?.uid === uid),
          ),
        )
        if (hasVariant) available.add(value.uid)
      }
    }
    return available
  }, [selection, sortedOptions, allVariants])

  const [selectionLabels, setSelectionLabels] = useState<Record<string, string>>({})

  const initializedRef = useRef(false)
  useEffect(() => {
    if (initializedRef.current || !sortedOptions.length) return
    initializedRef.current = true
    const initLabels: Record<string, string> = {}
    for (const option of sortedOptions) {
      const code = option?.attribute_code
      if (!code || !option.values?.length) continue
      const existingUid = (selection as any)[code] as string | undefined
      if (existingUid) {
        const match = option.values.find((v) => v?.uid === existingUid)
        if (match?.store_label) initLabels[code] = match.store_label
      } else {
        const first = option.values[0]
        if (first?.uid) {
          select((prev) => ({ ...prev, [code]: first.uid }) as any)
          if (first.store_label) initLabels[code] = first.store_label
        }
      }
    }
    setSelectionLabels(initLabels)
  }, [sortedOptions])

  const [expandedAttrs, setExpandedAttrs] = useState<Set<string>>(new Set())

  if (!sortedOptions.length) return null

  return (
    <div className='mb-6'>
      {sortedOptions.map((option) => {
        if (!option?.attribute_code || !option.values) return null
        const attrCode = option.attribute_code

        const allValues = option.values.filter((v) => v?.uid)
        const filtered =
          availableUids === null
            ? allValues
            : allValues.filter((v) => v?.uid && availableUids.has(v.uid))
        const availableValues = filtered.length > 0 ? filtered : allValues

        if (!availableValues.length) return null

        const hasColorSwatches = availableValues.some(
          (v) => v?.swatch_data?.__typename === 'ColorSwatchData',
        )
        const selectedColorLabel = hasColorSwatches
          ? availableValues.find(
              (v) =>
                v?.uid === (selection[attrCode] as unknown as string) &&
                (!selectionLabels[attrCode] || selectionLabels[attrCode] === v.store_label),
            )?.store_label
          : null

        const isThreeColTrigger =
          !hasColorSwatches &&
          (availableValues.length > 12 || option.label?.toLowerCase().includes('size'))
        const isExpanded = expandedAttrs.has(attrCode)

        return (
          <div key={attrCode} className='mb-6'>
            <p className='mb-3 text-[14px] font-medium uppercase tracking-[0.06em] text-[#8E8E93]'>
              {option.label}
              {selectedColorLabel && (
                <span className='font-medium text-black'>{` = ${selectedColorLabel.toUpperCase()}`}</span>
              )}
            </p>
            <div
              className={
                hasColorSwatches
                  ? 'grid grid-cols-4 gap-x-2 gap-y-5'
                  : isThreeColTrigger
                    ? 'grid grid-cols-1 gap-2 lg:grid-cols-3'
                    : 'grid grid-cols-1 gap-2 lg:grid-cols-2'
              }
            >
              {availableValues.map((value, idx) => {
                if (!value?.uid) return null
                const selectedUid = selection[attrCode] as unknown as string
                const isSelected =
                  selectedUid === value.uid &&
                  (!selectionLabels[attrCode] || selectionLabels[attrCode] === value.store_label)
                const isColor = value.swatch_data?.__typename === 'ColorSwatchData'
                const hiddenOnMobile = isThreeColTrigger && !isExpanded && idx >= 8
                return (
                  <div key={value.uid} className={hiddenOnMobile ? 'hidden lg:block' : undefined}>
                    {isColor ? (
                      <Tooltip title={value.store_label ?? ''} placement='top' arrow>
                        <div className='relative'>
                          <button
                            type='button'
                            aria-label={value.store_label ?? ''}
                            onClick={() => {
                              select((prev) => ({ ...prev, [attrCode]: value.uid }) as any)
                              setSelectionLabels((prev) => ({ ...prev, [attrCode]: value.store_label ?? '' }))
                            }}
                            className={`h-9 w-full rounded-[6px] transition-all active:scale-[0.95] ${
                              isSelected
                                ? 'ring-2 ring-[#5D54DE] ring-offset-2'
                                : 'ring-1 ring-[#d9d9d9] hover:ring-[#afafaf]'
                            }`}
                            style={{ backgroundColor: value.swatch_data?.value ?? '#ccc' }}
                          />
                          {isSelected && (
                            <span className='absolute left-1/2 top-full -translate-x-1/2 pt-0.5 whitespace-nowrap text-center text-[9px] font-medium leading-tight text-black'>
                              {value.store_label}
                            </span>
                          )}
                        </div>
                      </Tooltip>
                    ) : (
                      <button
                        type='button'
                        onClick={() => {
                          select((prev) => ({ ...prev, [attrCode]: value.uid }) as any)
                          setSelectionLabels((prev) => ({ ...prev, [attrCode]: value.store_label ?? '' }))
                        }}
                        className={`w-full overflow-hidden rounded-[4px] px-2.5 py-2.5 transition-all active:scale-[0.97] ${
                          isSelected
                            ? 'bg-[#D1CBCB33] text-black border-[1.6px] border-[#5D54DE] shadow-[0px_2px_3px_0px_rgba(0,0,0,0.25)]'
                            : 'bg-[#F8F8F8] text-black border-[1.4px] border-[#908B8B]'
                        }`}
                      >
                        <span className={`block truncate text-[13px] uppercase tracking-[0.04em] ${isSelected ? 'font-bold' : 'font-medium'}`}>
                          {value.store_label}
                        </span>
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
            {isThreeColTrigger && availableValues.length > 8 && (
              <button
                type='button'
                className='mt-2 text-[11px] font-medium text-[#5D54DE] lg:hidden'
                onClick={() =>
                  setExpandedAttrs((prev) => {
                    const next = new Set(prev)
                    if (isExpanded) next.delete(attrCode)
                    else next.add(attrCode)
                    return next
                  })
                }
              >
                {isExpanded ? 'Show fewer options' : `${availableValues.length - 8} more options`}
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
